import React, { useCallback, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  SearchIcon,
  PlusIcon,
  CloseIcon,
  ChevronDownIcon,
  EditIcon,
  DeleteIcon,
  MapPinIcon,
} from "../../../Icons";
import Table from "../../../components/UI/Table";
import {
  LIBRARIES_THEME,
  LIBRARIES_BTN,
  LIBRARIES_FIELD,
} from "../librariesTheme";
import LibraryStatusSelect from "../components/LibraryStatusSelect";
import LibraryBulkActionMenu from "../components/LibraryBulkActionMenu";
import LibraryDeleteConfirmModal from "../components/LibraryDeleteConfirmModal";
import CareFacilityFormModal from "./CareFacilityFormModal";
import useCareFacilities from "./useCareFacilities";
import { CARE_FACILITY_TYPES } from "../../../services/librariesCatalogService";

const cell = "text-[13px] text-slate-700 leading-none";
const cellMuted = "text-[13px] text-slate-600 leading-none";
const cellStrong = "text-[13px] font-medium text-slate-900 leading-none";

const CareFacilitiesPage = () => {
  const {
    items,
    loading,
    filters,
    setFilters,
    searchInput,
    setSearchInput,
    pagination,
    setPagination,
    selectedIds,
    toggleSelect,
    toggleSelectAll,
    clearFilters,
    create,
    update,
    remove,
    changeStatus,
    bulkChangeStatus,
  } = useCareFacilities();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [statusBusyId, setStatusBusyId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const hasActiveFilters = Boolean(
    filters.search || filters.status || filters.facilityType,
  );

  const handleSort = (key) => {
    setFilters((f) => ({
      ...f,
      sortBy: key,
      sortOrder: f.sortBy === key && f.sortOrder === "asc" ? "desc" : "asc",
    }));
    setPagination((p) => ({ ...p, page: 1 }));
  };

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setFormOpen(true);
  };

  const handleSave = async (payload) => {
    if (editing?.id) {
      await update(editing.id, payload);
    } else {
      await create(payload);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    try {
      await remove(deleteTarget.id);
      setDeleteTarget(null);
    } catch (err) {
      toast.error(err.message || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const handleStatusChange = useCallback(
    async (row, nextStatus) => {
      if (!nextStatus || nextStatus === row.status) return;
      setStatusBusyId(row.id);
      try {
        await changeStatus(row.id, nextStatus);
      } catch (err) {
        toast.error(err.message || "Status update failed");
      } finally {
        setStatusBusyId(null);
      }
    },
    [changeStatus],
  );

  const handleBulk = async (status) => {
    setBulkBusy(true);
    try {
      await bulkChangeStatus(status);
    } catch (err) {
      toast.error(err.message || "Bulk status update failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const allSelected =
    items.length > 0 && selectedIds.length === items.length;

  const columns = useMemo(
    () => [
      {
        header: (
          <input
            type="checkbox"
            checked={allSelected}
            onChange={toggleSelectAll}
            aria-label="Select all facilities"
            className="rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500"
          />
        ),
        accessor: "select",
        className: "w-10 pl-3 pr-1",
        render: (row) => (
          <input
            type="checkbox"
            checked={selectedIds.includes(row.id)}
            onChange={() => toggleSelect(row.id)}
            aria-label={`Select ${row.facilityName}`}
            className="rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500"
          />
        ),
      },
      {
        header: "Facility Name",
        accessor: "facilityName",
        sortable: true,
        className: "whitespace-nowrap px-3 min-w-[10rem]",
        render: (row) => (
          <span className={cellStrong}>{row.facilityName || "—"}</span>
        ),
      },
      {
        header: "Facility Type",
        accessor: "facilityType",
        sortable: true,
        className: "whitespace-nowrap px-3",
        render: (row) => (
          <span className={cell}>{row.facilityType || "—"}</span>
        ),
      },
      {
        header: "Facility Code",
        accessor: "facilityCode",
        sortable: true,
        className: "whitespace-nowrap px-3",
        render: (row) => (
          <span className={`${cellMuted} tabular-nums`}>
            {row.facilityCode || "—"}
          </span>
        ),
      },
      {
        header: "NPI Number",
        accessor: "npiNumber",
        sortable: true,
        className: "whitespace-nowrap px-3",
        render: (row) => (
          <span className={`${cellMuted} tabular-nums`}>
            {row.npiNumber || "—"}
          </span>
        ),
      },
      {
        header: "Status",
        accessor: "status",
        sortable: true,
        className: "whitespace-nowrap px-3",
        render: (row) => (
          <LibraryStatusSelect
            value={row.status}
            disabled={statusBusyId === row.id}
            onChange={(status) => handleStatusChange(row, status)}
          />
        ),
      },
      {
        header: "Actions",
        accessor: "actions",
        className: "whitespace-nowrap pl-3 pr-4",
        render: (row) => (
          <div className="inline-flex items-center gap-1">
            <button
              type="button"
              onClick={() => openEdit(row)}
              className={LIBRARIES_BTN.soft}
              title="Edit"
            >
              <EditIcon size={11} />
              Edit
            </button>
            <button
              type="button"
              onClick={() => setDeleteTarget(row)}
              className={LIBRARIES_BTN.danger}
              title="Delete"
              aria-label="Delete"
            >
              <DeleteIcon size={11} />
            </button>
          </div>
        ),
      },
    ],
    [
      allSelected,
      selectedIds,
      statusBusyId,
      toggleSelect,
      toggleSelectAll,
      handleStatusChange,
    ],
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <LibraryBulkActionMenu
          selectedCount={selectedIds.length}
          disabled={bulkBusy}
          onActivate={() => handleBulk("Active")}
          onDeactivate={() => handleBulk("Inactive")}
        />
        <button
          type="button"
          onClick={openCreate}
          className={LIBRARIES_BTN.primary}
        >
          <PlusIcon size={16} />
          Add Facility
        </button>
      </div>

      <div
        className={`bg-white rounded-xl border shadow-sm overflow-hidden ${LIBRARIES_THEME.section.wrap}`}
      >
        <div
          className={`flex items-center gap-2 h-11 px-4 border-b box-border ${LIBRARIES_THEME.section.head}`}
        >
          <span
            className={`w-0.5 h-4 rounded-full shrink-0 ${LIBRARIES_THEME.section.bar}`}
            aria-hidden
          />
          <span className={`shrink-0 ${LIBRARIES_THEME.section.icon}`}>
            <MapPinIcon size={14} />
          </span>
          <h3
            className={`min-w-0 flex-1 text-xs font-semibold uppercase tracking-wide truncate ${LIBRARIES_THEME.section.title}`}
          >
            Care Facilities
          </h3>
          {typeof pagination.total === "number" ? (
            <span
              className={`inline-flex items-center justify-center h-5 min-w-[1.25rem] px-1.5 rounded text-[10px] font-semibold tabular-nums shrink-0 ${LIBRARIES_THEME.section.badge}`}
            >
              {pagination.total}
            </span>
          ) : null}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 px-3 py-2.5 border-b border-indigo-100">
          <div className="relative flex-1 min-w-0">
            <SearchIcon
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
            <input
              type="text"
              placeholder="Search by name, code, NPI..."
              className={LIBRARIES_FIELD.search}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            {searchInput ? (
              <button
                type="button"
                onClick={() => setSearchInput("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-slate-400 hover:text-slate-600"
                aria-label="Clear search"
              >
                <CloseIcon size={14} />
              </button>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <div className="relative">
              <label className="sr-only" htmlFor="facility-type-filter">
                Filter by type
              </label>
              <select
                id="facility-type-filter"
                className={`${LIBRARIES_FIELD.select} min-w-[140px] ${
                  filters.facilityType ? LIBRARIES_FIELD.selectActive : ""
                }`}
                value={filters.facilityType}
                onChange={(e) => {
                  setFilters((f) => ({
                    ...f,
                    facilityType: e.target.value,
                  }));
                  setPagination((p) => ({ ...p, page: 1 }));
                }}
              >
                <option value="">All Types</option>
                {CARE_FACILITY_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              <ChevronDownIcon
                size={14}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
            </div>

            <div className="relative">
              <label className="sr-only" htmlFor="facility-status-filter">
                Filter by status
              </label>
              <select
                id="facility-status-filter"
                className={`${LIBRARIES_FIELD.select} ${
                  filters.status ? LIBRARIES_FIELD.selectActive : ""
                }`}
                value={filters.status}
                onChange={(e) => {
                  setFilters((f) => ({ ...f, status: e.target.value }));
                  setPagination((p) => ({ ...p, page: 1 }));
                }}
              >
                <option value="">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
              <ChevronDownIcon
                size={14}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
            </div>

            {hasActiveFilters ? (
              <button
                type="button"
                onClick={clearFilters}
                className={LIBRARIES_BTN.clear}
              >
                Clear
              </button>
            ) : null}
          </div>
        </div>

        <Table
          columns={columns}
          data={items}
          loading={loading}
          showSrNo={false}
          density="compact"
          embedded
          headTone={LIBRARIES_THEME.tableHead}
          sortKey={filters.sortBy}
          sortOrder={filters.sortOrder}
          onSort={handleSort}
          pagination={{
            currentPage: pagination.page,
            totalPages: pagination.totalPages,
            totalItems: pagination.total,
            itemsPerPage: pagination.limit,
            onPageChange: (page) => setPagination((p) => ({ ...p, page })),
          }}
          noDataMessage="No care facilities found. Add one to get started."
        />
      </div>

      <CareFacilityFormModal
        isOpen={formOpen}
        record={editing}
        onClose={() => setFormOpen(false)}
        onSave={handleSave}
      />

      <LibraryDeleteConfirmModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => !deleting && setDeleteTarget(null)}
        onConfirm={handleDelete}
        isLoading={deleting}
        title="Delete Care Facility"
        message={
          deleteTarget
            ? `Delete “${deleteTarget.facilityName}”? This cannot be undone.`
            : undefined
        }
      />
    </div>
  );
};

export default CareFacilitiesPage;
